/**
 * Diagrams Service
 *
 * Frontend service for the Diagrams feature. Calls the backend Diagrams proxy
 * (which holds the GitLab token server-side) via the shared API Gateway under
 * `/api/diagrams`. The browser never holds a GitLab token.
 *
 * The API is storage-agnostic: diagrams are addressed by an opaque Diagram_Id
 * and an opaque version string (the last commit ID that touched the file), so
 * this module knows nothing about GitLab internals. GitLab-specific URL
 * building (raw image URLs, file pages, embed snippets) is done from the
 * `RepoInfo` the backend returns with each diagram, never from hard-coded paths.
 *
 * Mirrors `gitlabService.ts`: a cached API base resolved from `VITE_API_URL`
 * or `aws-exports.json`, a bearer token passed in by the caller (obtained from
 * `useAuth`'s `getAccessToken`), typed responses, and a `DiagramsApiError`
 * carrying the HTTP status and the backend error `code`.
 *
 * Routes:
 *   GET    /api/diagrams                                 listDiagrams
 *   POST   /api/diagrams/ids                             reserveDiagramId
 *   POST   /api/diagrams                                 createDiagram
 *   GET    /api/diagrams/{id}                            getDiagram
 *   PUT    /api/diagrams/{id}                            saveDiagram
 *   DELETE /api/diagrams/{id}                            deleteDiagram
 *   GET    /api/diagrams/{id}/history                    getHistory
 *   GET    /api/diagrams/{id}/versions/{commitId}        getVersion
 *   GET    /api/diagrams/{id}/thumbnail                  getThumbnail (Blob)
 *   GET    /api/diagrams/{id}/lock                        getLock
 *   POST   /api/diagrams/{id}/lock                        acquireLock / renewLock / forceAcquireLock
 *   POST   /api/diagrams/{id}/lock/takeover               requestTakeover
 *   DELETE /api/diagrams/{id}/lock                        releaseLock
 *   GET    /api/diagrams/library                          getLibraries
 */

// ---------------------------------------------------------------------------
// Shared Types
// ---------------------------------------------------------------------------

/**
 * The Diagram_Kind: which kind of diagram a row is, and so which editor opens
 * it and how it is stored.
 * - `"excalidraw"`: one `.excalidraw.svg` file whose SVG embeds the editable
 *   Excalidraw scene.
 * - `"mermaid"`: a `.mermaid.mmd` Mermaid source plus a rendered `.mermaid.svg`,
 *   committed together; the source is the editable file.
 *
 * A single union type so adding a third kind later is a one-line change here.
 */
export type DiagramKind = "excalidraw" | "mermaid"

/**
 * The live Edit_Lock summary shown next to a diagram in list responses. Present
 * only when someone currently holds a live lock. The lock token is never
 * included here; it is only ever returned to the lock's own holder.
 */
export interface DiagramLockSummary {
  /** Display name of the user currently editing the diagram. */
  holderName: string
  /** Email of the user currently editing the diagram. */
  holderEmail: string
  /** ISO 8601 timestamp of when the current holder acquired the lock. */
  since: string
}

/**
 * A single diagram's metadata, as returned by the list and per-diagram routes.
 * The SVG content itself is returned separately (see `GetDiagramResponse`).
 */
export interface DiagramSummary {
  /** The Diagram_Id: an 8-character lowercase base-36 identifier, immutable. */
  id: string
  /** The Diagram_Kind, which decides the editor and how the diagram is stored. */
  kind: DiagramKind
  /** The human-readable Diagram_Title, stored inside the file. */
  title: string
  /** The Diagram_Folder (up to four segments joined by "/"), or null when the diagram is unfiled. */
  folder: string | null
  /** The Diagram_Path within the repo. The app never changes it, but a move in GitLab does. */
  path: string
  /** The Version: the ID of the last commit that touched the file. */
  version: string
  /** ISO 8601 timestamp of the last commit that touched the file. */
  updatedAt: string
  /** Display name of the author of the last commit. */
  updatedByName: string
  /** Email of the author of the last commit. */
  updatedByEmail: string
  /** The live Edit_Lock holder, or null when no one is editing. */
  lock: DiagramLockSummary | null
}

/**
 * Information about the Diagram_Repo, returned with list and get responses so
 * the frontend can build GitLab URLs (raw images, file pages) without
 * hard-coding the project path.
 */
export interface RepoInfo {
  /** The project's GitLab web URL, e.g. https://gitlab.com/group/diagrams. */
  webUrl: string
  /** The default branch diagrams live on, e.g. "main". */
  branch: string
}

/**
 * A pending Takeover request recorded on a live Edit_Lock.
 */
export interface TakeoverRequest {
  /** Display name of the user who requested the Takeover. */
  byName: string
  /** Email of the user who requested the Takeover. */
  byEmail: string
  /** ISO 8601 timestamp of when the Takeover was requested. */
  at: string
}

/**
 * The full status of an Edit_Lock, as returned by the lock status route. The
 * lock token is never part of this shape; it is returned only to the holder by
 * the acquire/renew route.
 */
export interface LockStatus {
  /** Display name of the current lock holder. */
  holderName: string
  /** Email of the current lock holder. */
  holderEmail: string
  /** ISO 8601 timestamp of when the holder acquired the lock. */
  since: string
  /** ISO 8601 timestamp of when the lock currently expires. */
  expiresAt: string
  /** A pending Takeover request, or null when none is pending. */
  takeoverRequest: TakeoverRequest | null
}

/**
 * A single commit in a diagram's history.
 */
export interface DiagramHistoryEntry {
  /** The full 40-character commit ID. */
  commitId: string
  /** The abbreviated commit ID GitLab shows in its UI. */
  shortId: string
  /** The commit message. */
  message: string
  /** Display name of the commit author. */
  authorName: string
  /** ISO 8601 timestamp of when the commit was authored. */
  authoredAt: string
  /** The commit's page URL in GitLab. */
  webUrl: string
}

/**
 * One team icon library's name, as listed by the library route. The file name
 * without its `.excalidrawlib` extension (e.g. "map-icons").
 */
export interface LibrarySummary {
  /** The library name (file name without extension). */
  name: string
}

/**
 * The payload for creating a diagram. The SVG must already carry the given id
 * (the backend checks that the SVG's embedded id matches `id`).
 */
export interface CreateDiagramRequest {
  /** The reserved Diagram_Id this SVG was exported with. */
  id: string
  /** The Diagram_Kind being created; the backend defaults to `"excalidraw"`. */
  kind: DiagramKind
  /** The Diagram_Title (1–200 characters). */
  title: string
  /** The Diagram_Folder (up to four segments joined by "/"), or null for an unfiled diagram. */
  folder: string | null
  /**
   * The indexed Diagram_File SVG text: for `"excalidraw"` the SVG with the
   * embedded scene; for `"mermaid"` the rendered `.mermaid.svg` picture.
   */
  svg: string
  /**
   * The Mermaid_Source text, required when `kind` is `"mermaid"` (the editable
   * `.mermaid.mmd`). Omitted for `"excalidraw"`, whose source is in the SVG.
   */
  source?: string
  /** An optional commit message; the backend defaults to `Create "{title}"`. */
  commitMessage?: string
}

/**
 * The payload for saving (updating) an existing diagram.
 */
export interface SaveDiagramRequest {
  /**
   * The indexed Diagram_File SVG text: for `"excalidraw"` the SVG with the
   * embedded scene; for `"mermaid"` the freshly rendered `.mermaid.svg`.
   */
  svg: string
  /**
   * The Mermaid_Source text, sent for a `"mermaid"` diagram so the `.mermaid.mmd`
   * is updated alongside the rendered SVG. Omitted for `"excalidraw"`.
   */
  source?: string
  /** The current Diagram_Title (a rename is committed with the save). */
  title: string
  /** The Base_Version the editor loaded; GitLab rejects a stale save. */
  baseVersion: string
  /** The caller's Edit_Lock token; the save is refused without the live lock. */
  lockToken: string
  /** The commit message (the editor prefills a default, editable by the user). */
  commitMessage: string
}

/**
 * The response from the list route.
 */
export interface ListDiagramsResponse {
  /** Every diagram in the repo, with its live lock joined in. */
  items: DiagramSummary[]
  /** The repo info for building GitLab URLs. */
  repo: RepoInfo
}

/**
 * The response from the get-single-diagram route.
 */
export interface GetDiagramResponse {
  /** The diagram's metadata (including its current Version and Diagram_Kind). */
  diagram: DiagramSummary
  /** The indexed Diagram_File SVG text (the picture; for excalidraw also the scene). */
  svg: string
  /**
   * The Mermaid_Source text, present only when the diagram's `kind` is
   * `"mermaid"`. The Mermaid editor edits this, not the rendered `svg`.
   */
  source?: string
  /** The repo info for building GitLab URLs. */
  repo: RepoInfo
}

/**
 * The response from the history route (one page).
 */
export interface GetHistoryResponse {
  /** The commits on this page, newest first. */
  items: DiagramHistoryEntry[]
  /** The next page number, or null when this is the last page. */
  nextPage: number | null
}

/**
 * The response from the version route.
 */
export interface GetVersionResponse {
  /**
   * The Diagram_File SVG text at the requested commit. Present for an
   * `"excalidraw"` diagram (its scene is embedded in the SVG).
   */
  svg?: string
  /**
   * The Mermaid_Source text at the requested commit. Present for a `"mermaid"`
   * diagram, whose editable content is the source, not the rendered SVG.
   */
  source?: string
  /** The commit ID the content was read at (echoes the request). */
  commitId: string
}

/**
 * The response from acquiring, renewing, or force-acquiring a lock. The token
 * is returned only to the holder and must be presented on save and release.
 */
export interface LockGrant {
  /** The opaque lock token to present on heartbeat, save, and release. */
  lockToken: string
  /** ISO 8601 timestamp of when the lock currently expires. */
  expiresAt: string
  /** A pending Takeover request, or null when none is pending. */
  takeoverRequest: TakeoverRequest | null
}

/**
 * The response from requesting a Takeover of someone else's lock.
 */
export interface TakeoverResponse {
  /** ISO 8601 timestamp after which the requester may force-acquire the lock. */
  graceEndsAt: string
}

/**
 * The response from the single-library route.
 */
export interface GetLibraryResponse {
  /** The library name (file name without extension). */
  name: string
  /**
   * The Excalidraw library items, as stored in the `.excalidrawlib` file.
   * Kept as `unknown[]` here because the shape is owned by Excalidraw; the
   * editor passes these straight to `restoreLibraryItems`.
   */
  libraryItems: unknown[]
}

// ---------------------------------------------------------------------------
// Error Type
// ---------------------------------------------------------------------------

/**
 * An error thrown when a Diagrams API call returns a non-2xx response. Carries
 * the HTTP status and the backend's machine-readable error `code` (e.g.
 * "VERSION_CONFLICT", "LOCKED", "ID_TAKEN"), so callers can branch on the code
 * rather than parsing the message. The message is the backend's human-readable
 * `error` string when present.
 */
export class DiagramsApiError extends Error {
  /** The HTTP status code of the failed response. */
  public readonly status: number
  /** The backend's machine-readable error code, or null if none was given. */
  public readonly code: string | null

  /**
   * Construct a DiagramsApiError.
   *
   * @param params - The error details.
   * @param params.status - The HTTP status code of the failed response.
   * @param params.code - The backend error code, or null when absent.
   * @param params.message - The human-readable error message.
   */
  constructor({
    status,
    code,
    message,
  }: {
    status: number
    code: string | null
    message: string
  }) {
    super(message)
    this.name = "DiagramsApiError"
    this.status = status
    this.code = code
    // Restore the prototype chain so `instanceof DiagramsApiError` works after
    // TypeScript downlevels the class (a known quirk of extending Error).
    Object.setPrototypeOf(this, DiagramsApiError.prototype)
  }
}

// ---------------------------------------------------------------------------
// API URL Resolution
// ---------------------------------------------------------------------------

/** Cached API base URL for the Diagrams API, resolved on first use. */
let DIAGRAMS_API_URL: string = ""

/**
 * Resolve the Diagrams API base URL, caching it after the first call.
 *
 * Uses the `VITE_API_URL` env var if set (local dev with --host), otherwise the
 * `apiUrl` baked into `aws-exports.json` for deployed builds, and finally a
 * relative path as a last resort. Mirrors `gitlabService.ts`.
 *
 * @returns The base URL for the Diagrams API (e.g. "https://host/api/diagrams").
 * @throws Error if the configuration cannot be loaded.
 */
async function loadApiUrl(): Promise<string> {
  if (DIAGRAMS_API_URL) {
    return DIAGRAMS_API_URL
  }

  // VITE_API_URL is available at build time in deployed/dev-host mode.
  const envUrl: string = import.meta.env.VITE_API_URL || ""
  if (envUrl) {
    DIAGRAMS_API_URL = `${envUrl}/api/diagrams`
    return DIAGRAMS_API_URL
  }

  // Fall back to aws-exports.json for CI-built deployments.
  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = config.apiUrl || ""
    DIAGRAMS_API_URL = baseUrl ? `${baseUrl}/api/diagrams` : "/api/diagrams"
    return DIAGRAMS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Diagrams API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// Request Helpers
// ---------------------------------------------------------------------------

/**
 * Build the standard request headers for a Diagrams API call.
 *
 * @param params - The header inputs.
 * @param params.token - The Auth0 access token for the Authorization header.
 * @param params.json - Whether to add a JSON Content-Type (for bodies).
 * @returns A headers object with the bearer token and, optionally, JSON type.
 */
function buildHeaders({ token, json }: { token: string; json: boolean }): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  }
  if (json) {
    headers["Content-Type"] = "application/json"
  }
  return headers
}

/**
 * Turn a non-2xx response into a DiagramsApiError, reading the backend's
 * `{ error, code }` JSON body when present. Always throws; never returns.
 *
 * @param response - The failed fetch Response (response.ok is false).
 * @throws DiagramsApiError carrying the status, code, and message.
 */
async function throwApiError(response: Response): Promise<never> {
  // The backend sends `{ error, code }`; tolerate a non-JSON body (e.g. a
  // gateway error page) by falling back to the status.
  const body: { error?: string; code?: string } = await response
    .json()
    .catch(() => ({}) as { error?: string; code?: string })

  throw new DiagramsApiError({
    status: response.status,
    code: body.code ?? null,
    message: body.error ?? `Diagrams API request failed: HTTP ${response.status}`,
  })
}

/**
 * Perform a JSON Diagrams API request and parse the JSON response, throwing a
 * DiagramsApiError on any non-2xx status.
 *
 * @param params - The request inputs.
 * @param params.path - The path relative to the Diagrams API base (e.g. "/ids").
 * @param params.method - The HTTP method.
 * @param params.token - The Auth0 access token.
 * @param params.body - An optional JSON-serialisable request body.
 * @returns The parsed JSON response, typed as T.
 * @throws DiagramsApiError if the response status is not 2xx.
 */
async function requestJson<T>({
  path,
  method,
  token,
  body,
}: {
  path: string
  method: "GET" | "POST" | "PUT" | "DELETE"
  token: string
  body?: unknown
}): Promise<T> {
  const apiUrl: string = await loadApiUrl()

  const response = await fetch(`${apiUrl}${path}`, {
    method,
    headers: buildHeaders({ token, json: body !== undefined }),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    await throwApiError(response)
  }

  return (await response.json()) as T
}

/**
 * Encode a path segment (Diagram_Id, commit ID, library name) for safe use in
 * a URL path. These values are backend-validated, but encoding keeps a stray
 * character from breaking the URL.
 *
 * @param segment - The raw path segment.
 * @returns The percent-encoded segment.
 */
function encodeSegment(segment: string): string {
  return encodeURIComponent(segment)
}

// ---------------------------------------------------------------------------
// API Calls — Diagrams
// ---------------------------------------------------------------------------

/**
 * List every diagram in the repo (the backend resynchronises its index first)
 * along with repo info for building GitLab URLs.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The diagrams and the repo info.
 * @throws DiagramsApiError if the API call fails.
 */
export async function listDiagrams(token: string): Promise<ListDiagramsResponse> {
  return requestJson<ListDiagramsResponse>({ path: "", method: "GET", token })
}

/**
 * Reserve an unused Diagram_Id so the browser can export the new Diagram_File
 * with its id embedded before creating it. Nothing is stored; the id is checked
 * again on create.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The reserved, currently-unused Diagram_Id.
 * @throws DiagramsApiError if the API call fails.
 */
export async function reserveDiagramId(token: string): Promise<string> {
  const data = await requestJson<{ id: string }>({ path: "/ids", method: "POST", token })
  return data.id
}

/**
 * Create a diagram from an already-exported Diagram_File SVG. The backend
 * checks the id is unused and matches the SVG, builds the path, and commits a
 * `create` action.
 *
 * @param token - Auth0 access token for API authentication.
 * @param request - The id, kind, title, folder, SVG, optional Mermaid source,
 *   and optional commit message.
 * @returns The created diagram's metadata.
 * @throws DiagramsApiError if the API call fails (e.g. 409 `ID_TAKEN`, 413).
 */
export async function createDiagram(
  token: string,
  request: CreateDiagramRequest,
): Promise<DiagramSummary> {
  const data = await requestJson<{ diagram: DiagramSummary }>({
    path: "",
    method: "POST",
    token,
    body: {
      id: request.id,
      kind: request.kind,
      title: request.title,
      folder: request.folder,
      svg: request.svg,
      // Only send `source` for a mermaid diagram (the `.mermaid.mmd` text); an
      // excalidraw diagram has no separate source.
      ...(request.source !== undefined ? { source: request.source } : {}),
      // Only send commitMessage when provided, so the backend can apply its
      // own `Create "{title}"` default rather than receiving an empty string.
      ...(request.commitMessage !== undefined ? { commitMessage: request.commitMessage } : {}),
    },
  })
  return data.diagram
}

/**
 * Get the current Diagram_File, its Version, and its metadata.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id to fetch.
 * @returns The diagram metadata, its SVG text, and the repo info.
 * @throws DiagramsApiError if the API call fails (e.g. 404).
 */
export async function getDiagram(token: string, diagramId: string): Promise<GetDiagramResponse> {
  return requestJson<GetDiagramResponse>({
    path: `/${encodeSegment(diagramId)}`,
    method: "GET",
    token,
  })
}

/**
 * Save (update) an existing diagram. The backend requires the caller to hold
 * the live Edit_Lock and rejects a stale Base_Version.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id to save.
 * @param request - The SVG, optional Mermaid source, title, Base_Version, lock
 *   token, and commit message.
 * @returns The updated diagram's metadata (with its new Version).
 * @throws DiagramsApiError if the API call fails (e.g. 409 `VERSION_CONFLICT`,
 *   423 `LOCK_NOT_HELD`, 413).
 */
export async function saveDiagram(
  token: string,
  diagramId: string,
  request: SaveDiagramRequest,
): Promise<DiagramSummary> {
  const data = await requestJson<{ diagram: DiagramSummary }>({
    path: `/${encodeSegment(diagramId)}`,
    method: "PUT",
    token,
    body: {
      svg: request.svg,
      // Only send `source` for a mermaid diagram, so the `.mermaid.mmd` is
      // updated alongside the rendered SVG; omitted for excalidraw.
      ...(request.source !== undefined ? { source: request.source } : {}),
      title: request.title,
      baseVersion: request.baseVersion,
      lockToken: request.lockToken,
      commitMessage: request.commitMessage,
    },
  })
  return data.diagram
}

/**
 * Delete a diagram. The backend refuses with 423 `LOCKED_BY_OTHER` when
 * someone else holds a live lock, and 409 `VERSION_CONFLICT` if the file
 * changed since `baseVersion`.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id to delete.
 * @param baseVersion - The Version the client last saw, passed as `last_commit_id`.
 * @returns Nothing on success.
 * @throws DiagramsApiError if the API call fails.
 */
export async function deleteDiagram(
  token: string,
  diagramId: string,
  baseVersion: string,
): Promise<void> {
  const apiUrl: string = await loadApiUrl()

  const url = new URL(
    `${apiUrl}/${encodeSegment(diagramId)}`,
    window.location.origin,
  )
  url.searchParams.set("baseVersion", baseVersion)

  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: buildHeaders({ token, json: false }),
  })

  if (!response.ok) {
    await throwApiError(response)
  }
}

// ---------------------------------------------------------------------------
// API Calls — History and Versions
// ---------------------------------------------------------------------------

/**
 * Get one page of a diagram's commit history (20 per page, newest first).
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id whose history to list.
 * @param page - The 1-based page number. Defaults to 1.
 * @returns The page of history entries and the next page number (or null).
 * @throws DiagramsApiError if the API call fails.
 */
export async function getHistory(
  token: string,
  diagramId: string,
  page: number = 1,
): Promise<GetHistoryResponse> {
  const apiUrl: string = await loadApiUrl()

  const url = new URL(
    `${apiUrl}/${encodeSegment(diagramId)}/history`,
    window.location.origin,
  )
  url.searchParams.set("page", String(page))

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: buildHeaders({ token, json: false }),
  })

  if (!response.ok) {
    await throwApiError(response)
  }

  return (await response.json()) as GetHistoryResponse
}

/**
 * Get a diagram's SVG as it was at a specific commit.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id to read.
 * @param commitId - The 40-character hex commit ID to read the file at.
 * @returns The SVG text at that commit and the echoed commit ID.
 * @throws DiagramsApiError if the API call fails.
 */
export async function getVersion(
  token: string,
  diagramId: string,
  commitId: string,
): Promise<GetVersionResponse> {
  return requestJson<GetVersionResponse>({
    path: `/${encodeSegment(diagramId)}/versions/${encodeSegment(commitId)}`,
    method: "GET",
    token,
  })
}

/**
 * Get a diagram's thumbnail: the current Diagram_File with the embedded scene
 * stripped, as an `image/svg+xml` body. Returned as a Blob so the caller can
 * create an object URL for an `<img>`.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id whose thumbnail to fetch.
 * @returns The thumbnail SVG as a Blob.
 * @throws DiagramsApiError if the API call fails.
 */
export async function getThumbnail(token: string, diagramId: string): Promise<Blob> {
  const apiUrl: string = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${encodeSegment(diagramId)}/thumbnail`, {
    method: "GET",
    headers: buildHeaders({ token, json: false }),
  })

  if (!response.ok) {
    await throwApiError(response)
  }

  return await response.blob()
}

// ---------------------------------------------------------------------------
// API Calls — Locks
// ---------------------------------------------------------------------------

/**
 * Get the current Edit_Lock status for a diagram, or null when no one holds it.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id whose lock status to read.
 * @returns The lock status, or null when the diagram is not locked.
 * @throws DiagramsApiError if the API call fails.
 */
export async function getLock(token: string, diagramId: string): Promise<LockStatus | null> {
  const data = await requestJson<{ lock: LockStatus | null }>({
    path: `/${encodeSegment(diagramId)}/lock`,
    method: "GET",
    token,
  })
  return data.lock
}

/**
 * Acquire the Edit_Lock for a diagram (no existing token). On failure the
 * backend returns 409 `LOCKED` with the current `LockStatus`, surfaced as a
 * DiagramsApiError whose `code` is "LOCKED".
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id to lock.
 * @returns The lock grant (token, expiry, any pending takeover).
 * @throws DiagramsApiError if the API call fails (e.g. 409 `LOCKED`).
 */
export async function acquireLock(token: string, diagramId: string): Promise<LockGrant> {
  return requestJson<LockGrant>({
    path: `/${encodeSegment(diagramId)}/lock`,
    method: "POST",
    token,
    body: {},
  })
}

/**
 * Renew (heartbeat) the Edit_Lock the caller already holds. On failure (the
 * lock expired or was taken over) the backend returns 409 `LOCK_LOST`.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id whose lock to renew.
 * @param lockToken - The lock token the caller holds.
 * @returns The refreshed lock grant (token, new expiry, any pending takeover).
 * @throws DiagramsApiError if the API call fails (e.g. 409 `LOCK_LOST`).
 */
export async function renewLock(
  token: string,
  diagramId: string,
  lockToken: string,
): Promise<LockGrant> {
  return requestJson<LockGrant>({
    path: `/${encodeSegment(diagramId)}/lock`,
    method: "POST",
    token,
    body: { lockToken },
  })
}

/**
 * Force-acquire the Edit_Lock after the Takeover_Grace has passed. Succeeds
 * only for the user who requested the Takeover and only after the grace period.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id to force-acquire.
 * @returns The lock grant (token, expiry, any pending takeover).
 * @throws DiagramsApiError if the API call fails.
 */
export async function forceAcquireLock(token: string, diagramId: string): Promise<LockGrant> {
  return requestJson<LockGrant>({
    path: `/${encodeSegment(diagramId)}/lock`,
    method: "POST",
    token,
    body: { force: true },
  })
}

/**
 * Request a Takeover of a lock someone else holds. The backend records the
 * requester on the lock and returns when the grace period ends. On 409
 * `NOT_LOCKED` the caller should just acquire the lock instead.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id whose lock to take over.
 * @returns The time after which the requester may force-acquire the lock.
 * @throws DiagramsApiError if the API call fails (e.g. 409 `NOT_LOCKED`).
 */
export async function requestTakeover(
  token: string,
  diagramId: string,
): Promise<TakeoverResponse> {
  return requestJson<TakeoverResponse>({
    path: `/${encodeSegment(diagramId)}/lock/takeover`,
    method: "POST",
    token,
  })
}

/**
 * Release the Edit_Lock the caller holds. Supports a `keepalive` option so the
 * release can be sent from a `pagehide`/unmount handler and still complete
 * while the page is tearing down.
 *
 * @param token - Auth0 access token for API authentication.
 * @param diagramId - The Diagram_Id whose lock to release.
 * @param lockToken - The lock token the caller holds.
 * @param options - Optional request options.
 * @param options.keepalive - When true, sets `fetch`'s `keepalive` flag so the
 *   request survives page teardown. Defaults to false.
 * @returns Nothing on success.
 * @throws DiagramsApiError if the API call fails (e.g. 409 `LOCK_NOT_HELD`).
 */
export async function releaseLock(
  token: string,
  diagramId: string,
  lockToken: string,
  options: { keepalive?: boolean } = {},
): Promise<void> {
  const apiUrl: string = await loadApiUrl()

  const url = new URL(
    `${apiUrl}/${encodeSegment(diagramId)}/lock`,
    window.location.origin,
  )
  url.searchParams.set("lockToken", lockToken)

  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: buildHeaders({ token, json: false }),
    keepalive: options.keepalive === true,
  })

  if (!response.ok) {
    await throwApiError(response)
  }
}

// ---------------------------------------------------------------------------
// API Calls — Libraries
// ---------------------------------------------------------------------------

/**
 * List the team icon libraries available in the repo, in the backend's order
 * (`map-icons` first, `aws-architecture-icons` second, the rest by name).
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The library summaries in display order.
 * @throws DiagramsApiError if the API call fails.
 */
export async function getLibraries(token: string): Promise<LibrarySummary[]> {
  const data = await requestJson<{ libraries: LibrarySummary[] }>({
    path: "/library",
    method: "GET",
    token,
  })
  return data.libraries
}

/**
 * Fetch a single team library's items by name (as listed by `getLibraries`).
 *
 * @param token - Auth0 access token for API authentication.
 * @param name - The library name (file name without extension).
 * @returns The library name and its Excalidraw library items.
 * @throws DiagramsApiError if the API call fails (e.g. 502 `INVALID_LIBRARY`).
 */
export async function getLibrary(token: string, name: string): Promise<GetLibraryResponse> {
  return requestJson<GetLibraryResponse>({
    path: `/library/${encodeSegment(name)}`,
    method: "GET",
    token,
  })
}

// ---------------------------------------------------------------------------
// URL and Embed Builders
// ---------------------------------------------------------------------------

/**
 * Build the raw URL of a Diagram_File on the repo's default branch. This is the
 * URL that always shows the latest committed version, used as the image source
 * in an Embed_Snippet.
 *
 * @param params - The URL inputs.
 * @param params.repo - The repo info (web URL and branch) from the API.
 * @param params.path - The Diagram_Path within the repo.
 * @returns The raw file URL, e.g. "https://gitlab.com/group/diagrams/-/raw/main/diagrams/x-abcd1234.excalidraw.svg".
 */
export function buildRawUrl({ repo, path }: { repo: RepoInfo; path: string }): string {
  // Strip a trailing slash from webUrl so the join never doubles up.
  const base: string = repo.webUrl.replace(/\/+$/, "")
  return `${base}/-/raw/${repo.branch}/${path}`
}

/**
 * Build the GitLab file page URL for a Diagram_File on the default branch, used
 * by the "Open in GitLab" action.
 *
 * @param params - The URL inputs.
 * @param params.repo - The repo info (web URL and branch) from the API.
 * @param params.path - The Diagram_Path within the repo.
 * @returns The file page URL, e.g. "https://gitlab.com/group/diagrams/-/blob/main/diagrams/x-abcd1234.excalidraw.svg".
 */
export function buildGitLabFileUrl({ repo, path }: { repo: RepoInfo; path: string }): string {
  const base: string = repo.webUrl.replace(/\/+$/, "")
  return `${base}/-/blob/${repo.branch}/${path}`
}

/**
 * Build the Embed_Snippet for a diagram: Markdown that shows the latest
 * committed Diagram_File as an image and links it to the editor.
 *
 * Format: `[![{title}]({raw URL on main})]({app URL}/diagrams/{id})`.
 *
 * @param params - The snippet inputs.
 * @param params.diagram - The diagram to embed (for its id, title, and path).
 * @param params.repo - The repo info, for the raw image URL.
 * @param params.appBaseUrl - The app's base URL for the current environment
 *   (e.g. "https://apps.malariaatlas.org"); a trailing slash is tolerated.
 * @returns The Markdown Embed_Snippet.
 */
export function buildEmbedSnippet({
  diagram,
  repo,
  appBaseUrl,
}: {
  diagram: DiagramSummary
  repo: RepoInfo
  appBaseUrl: string
}): string {
  const rawUrl: string = buildRawUrl({ repo, path: diagram.path })
  const appBase: string = appBaseUrl.replace(/\/+$/, "")
  const editorUrl: string = `${appBase}/diagrams/${diagram.id}`
  return `[![${diagram.title}](${rawUrl})](${editorUrl})`
}
